import { inject, Injectable } from '@angular/core';
import { SupabaseService } from '../../services/supabase.service';
import { createTopic } from './topic';
import { TablesInsert, TablesUpdate } from '../database.types';
import { TopicRepository } from './topic.repository';
import { ToastController } from '@ionic/angular';

@Injectable({providedIn: 'root'})
export class TopicApi {
  private toastController = inject(ToastController);

  constructor(private supabase: SupabaseService,
              private topicRepository: TopicRepository) {

  }

  async add(name: string) {
    return (await this.addMultiple([createTopic(name, 0)]))[0];
  }

  async addMultiple(topics: TablesInsert<'topics'>[]) {
    let currentTopicOrder = await this.getHighestOrder();
    topics = topics.map(topic => {
      currentTopicOrder++;
      return {
        ...topic,
        order_number: currentTopicOrder
      }
    })
    const {data, error} = await this.supabase.client
      .from('topics')
      .insert(topics)
      .select();

    if (!data) {
      throw Error();
    }

    this.topicRepository.upsert(data);
    return data;
  }

  async update(topicId: number, topic: TablesUpdate<'topics'>) {
    const {data, error} = await this.supabase.client
      .from('topics')
      .update(topic)
      .eq('id', topicId)
      .select()
      .single();

    if (!data) {
      throw Error();
    }

    if (topic.is_pinned) {
      this.topicRepository.removeTemp(topicId);
    }

    this.toastController.create({
      message: 'Thema wurde gespeichert.'
    }).then(toast => toast.present())

    return this.topicRepository.upsert([data]);
  }

  /**
   * implemented for reordering at the moment
   * @param topics
   */
  async upsertMultiple(topics: TablesInsert<'topics'>[]) {
    const {data, error} = await this.supabase.client
      .from('topics')
      .upsert(topics)
      .select()

    if (!data) {
      throw Error();
    }

    this.toastController.create({
      message: 'Thema wurde gespeichert.'
    }).then(toast => toast.present())

    return this.topicRepository.upsert(data);
  }

  async fetch() {
    const {data, error} = await this.supabase.client
      .from('topics')
      .select(`
        id,
        name,
        order_number,
        color,
        range_target,
        is_pinned,
        is_active_tracker,
        created_at,
        hidden
      `)

    if (!data) {
      throw Error();
    }

    this.topicRepository.set(data);
  }

  async topicsExist() {
    const {data, error} = await this.supabase.client
      .from('topics')
      .select('id')
      .limit(1)
      .maybeSingle();

    return !!data;
  }

  async getHighestOrder() {
    const {data, error} = await this.supabase.client
      .from('topics')
      .select('order_number')
      .not('order_number', 'is', null)
      .order('order_number', {ascending: false})
      .limit(1)
      .maybeSingle();

    return data?.order_number || 1;
  }
}
